/**
 * Desktop banner for a call that is ringing in this browser (t1383).
 *
 * The ringing call bar only helps while the portal is in front. When the tab is
 * hidden or another window has focus, this raises an OS notification through
 * the service worker; clicking it just brings the portal forward. It never
 * throws — a browser that can't show it simply rings as before.
 */
const CALL_TAG = 'incoming-call'

function portalInBackground(): boolean {
  return document.visibilityState !== 'visible' || !document.hasFocus()
}

export async function showIncomingCallNotification(from: string, waiting = false): Promise<void> {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return
    if (!('serviceWorker' in navigator) || !portalInBackground()) return
    const registration = await navigator.serviceWorker.getRegistration('/')
    if (!registration) return
    // `renotify` is valid in browsers but missing from the DOM lib's typings.
    const options: NotificationOptions & { renotify?: boolean } = {
      body: from,
      tag: CALL_TAG,
      renotify: true,
      requireInteraction: true,
      icon: '/icons/icon-192.png',
      badge: '/icons/badge-72.png',
      data: { url: window.location.pathname, focusOnly: true },
    }
    await registration.showNotification(waiting ? 'Call waiting' : 'Incoming call', options)
  } catch {
    // Best effort.
  }
}

export async function closeIncomingCallNotification(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return
    const registration = await navigator.serviceWorker.getRegistration('/')
    const open = (await registration?.getNotifications({ tag: CALL_TAG })) ?? []
    for (const notification of open) notification.close()
  } catch {
    // Best effort.
  }
}
